import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useMood } from '../theme/MoodContext';
import { moodForRoutine } from '../theme/moods';
import Typography from '../components/Text/Typography';
import Button from '../components/Button/Button';
import RoutineCard from '../components/RoutineCard/RoutineCard';
import ResponsiveDialog from '../components/ResponsiveDialog/ResponsiveDialog';
import EmptyCabinetState from '../components/EmptyCabinetState/EmptyCabinetState';
import './RoutinePage.css';
import { API_BASE_URL, authenticatedFetch } from '../config/api';
import { guestDataService } from '../services/guestDataService';
import { Routine } from '../types/routine';
import Toggle from "../components/Toggle/Toggle";
import NavigationTab from "../components/NavigationTab/NavigationTab";
import IconButton from "../components/Button/IconButton";
import Icon from "../components/Icon/Icon";
import { RecommendationsBottomSheet } from "../bottomdrawers";
import AddToRoutineDialog from "../dialogs/AddToRoutineDialog";
import { AddToRoutineBottomSheet } from "../bottomdrawers";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import Tooltip from "../components/Tooltip/Tooltip";

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ROUTINE_PRESETS: Record<
  string,
  { routine_type: string; routine_name: string; description: string; icon: string }
> = {
  'post-exercise': {
    routine_type: 'post-exercise',
    routine_name: 'Post-Exercise',
    description: 'Cool down after you move',
    icon: 'fitness',
  },
  'back-from-work': {
    routine_type: 'back-from-work',
    routine_name: 'Back from Work',
    description: 'Reset after the workday',
    icon: 'work',
  },
  'beach-day': {
    routine_type: 'beach-day',
    routine_name: 'Beach Day',
    description: 'Sun, salt, and recovery',
    icon: 'sun',
  },
  'after-night-out': {
    routine_type: 'after-night-out',
    routine_name: 'After a Night Out',
    description: 'Gentle late-night recovery',
    icon: 'moon',
  },
};

// Recommended category order from algorithm.js
const categoryOrder = {
  AM: [
    'cleanser',
    'toner',
    'facial mist',
    'serum',
    'treatment',
    'eye care',
    'all in one',
    'moisturizer',
    'sunscreen',
    'lip care'
  ],
  PM: [
    'cleanser',
    'toner',
    'facial mist',
    'face mask',
    'serum',
    'eye care',
    'treatment',
    'all in one',
    'moisturizer',
    'lip care'
  ]
};

const capitalizeWords = (str: string) => {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Function to check if a product is in the correct order
const checkProductOrder = (product: any, currentIndex: number, routineType: string, allProducts: any[]) => {
  if (!product.category) return { isCorrectOrder: true, expectedPosition: currentIndex + 1 };
  
  const category = product.category.toLowerCase();
  // For custom routines, we'll use PM order as default since most custom routines are evening-focused
  const order = categoryOrder.PM;
  const expectedIndex = order.indexOf(category);
  
  if (expectedIndex === -1) return { isCorrectOrder: true, expectedPosition: currentIndex + 1 };
  
  // Check if this category appears before any category that should come before it
  let hasOrderViolation = false;
  for (let i = 0; i < currentIndex; i++) {
    const previousProduct = allProducts[i];
    if (previousProduct && previousProduct.category) {
      const previousCategory = previousProduct.category.toLowerCase();
      const previousExpectedIndex = order.indexOf(previousCategory);
      
      // If the previous category should come after this category, that's a violation
      if (previousExpectedIndex !== -1 && previousExpectedIndex > expectedIndex) {
        hasOrderViolation = true;
        break;
      }
    }
  }
  
  return { 
    isCorrectOrder: !hasOrderViolation, 
    expectedPosition: expectedIndex + 1 
  };
};

const CustomRoutine = () => {
  const { routineType } = useParams<{ routineType: string }>();
  const [selectedDay, setSelectedDay] = useState(0);
  const [routine, setRoutine] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [showRecommendations, setShowRecommendations] = useState(() => {
    // Initialize from localStorage, default to false if not found
    const saved = localStorage.getItem('showRecommendations');
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddToRoutineDialogOpen, setIsAddToRoutineDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [customRoutineInfo, setCustomRoutineInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [daysWithoutActives, setDaysWithoutActives] = useState<Set<number>>(new Set());
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const { user } = useAuth();
  useMood(moodForRoutine(routineType));
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Check screen size for mobile responsiveness
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Fetch routine data when component mounts or user changes
  useEffect(() => {
    if (!routineType) {
      setRoutine([]);
      setCustomRoutineInfo(null);
      setIsLoading(false);
      return;
    }
    
    fetchCustomRoutineInfo();
    fetchRoutineData();
  }, [user, selectedDay, routineType]);

  // Check which days have no products with active ingredients
  useEffect(() => {
    if (!routineType) return;
    
    const checkDaysForActives = async () => {
      const daysWithoutActivesSet = new Set<number>();
      
      for (let i = 0; i < days.length; i++) {
        const dayOfWeek = days[i];
        try {
          const dayRoutine = await guestDataService.getRoutine(user, routineType, dayOfWeek);
          
          // If no routine exists and user is authenticated, try to fetch from API
          let routineToCheck = dayRoutine;
          if (user?.id && (!routineToCheck || routineToCheck.length === 0)) {
            try {
              const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${user.id}/${routineType}/${dayOfWeek}`);
              if (response.ok) {
                routineToCheck = await response.json();
              }
            } catch (error) {
              console.error(`Error fetching routine for ${dayOfWeek}:`, error);
            }
          }
          
          // Check if any product in the routine has active ingredients
          const hasActives = routineToCheck && routineToCheck.some((item: any) => {
            // Check if it's a product (has product_id) and has actives
            if (item.product_id && item.actives) {
              const activesStr = item.actives.trim();
              return activesStr.length > 0 && activesStr !== 'null' && activesStr !== 'NULL';
            }
            return false;
          });
          
          // If no products with actives, add this day to the set
          if (!hasActives) {
            daysWithoutActivesSet.add(i);
          }
        } catch (error) {
          console.error(`Error checking actives for ${dayOfWeek}:`, error);
        }
      }
      
      setDaysWithoutActives(daysWithoutActivesSet);
    };
    
    if (user !== undefined) {
      checkDaysForActives();
    }
  }, [user, routineType, days]);

  const fetchCustomRoutineInfo = async () => {
    if (!routineType) return;
    
    try {
      const routines = await guestDataService.getCustomRoutines(user);
      const routineInfo = routines.find((r: any) => r.routine_type === routineType);
      setCustomRoutineInfo(routineInfo || ROUTINE_PRESETS[routineType] || null);
    } catch (error) {
      console.error('Error fetching custom routine info:', error);
    }
  };

  const fetchRoutineData = async () => {
    if (!routineType) return;
    
    setIsLoading(true);
    try {
      const currentDay = days[selectedDay];
      const routineData = await guestDataService.getRoutine(user, routineType, currentDay);
      setRoutine(routineData || []);
    } catch (error) {
      console.error('Error fetching routine:', error);
      setRoutine([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!user?.id || !routineType) return;
    
    try {
      const currentDay = days[selectedDay];
      const response = await authenticatedFetch(
                  `${API_BASE_URL}/api/user/routines/${user.id}/${routineType}/${currentDay}/products/${productId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        // Refresh routine data
        await fetchRoutineData();
      } else {
        const errorData = await response.json();
        alert(`Failed to remove product: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error removing product:', error);
      alert('Failed to remove product from routine');
    }
  };

  const handleAddProductToRoutine = async (productId: number) => {
    if (!user?.id || !routineType) return;
    try {
      const currentDay = days[selectedDay];
      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/user/routines/${user.id}/${routineType}/${currentDay}/products`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId }),
        }
      );
      if (response.ok) {
        await fetchRoutineData();
        setIsAddToRoutineDialogOpen(false);
      }
    } catch (error) {
      console.error('Error adding product to routine:', error);
    }
  };

  const handleAddActionToRoutine = async (actionId: string) => {
    if (!user?.id || !routineType) return;

    try {
      const currentDay = days[selectedDay];
      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/user/routines/${user.id}/${routineType}/${currentDay}/actions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ actionId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add action to routine');
      }

      await fetchRoutineData();
      setIsAddToRoutineDialogOpen(false);
    } catch (error) {
      console.error('Error adding action to routine:', error);
      showToast('Failed to add action to routine', 'error');
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    try {
      const currentDay = days[selectedDay];
      const draggedProduct = routine[draggedIndex];
      
      // Remove the product from its current position
      await handleDeleteProduct(draggedProduct.product_id);
      
      // Add it back at the new position
      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/user/routines/${user.id}/${routineType}/${currentDay}/products`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId: draggedProduct.product_id })
        }
      );
      
      if (response.ok) {
        await fetchRoutineData();
      }
    } catch (error) {
      console.error('Error reordering product:', error);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleAddProductToCabinet = async (productId: number) => {
    try {
      const success = await guestDataService.addProduct(user, productId);
      if (success) showToast('Product added to cabinet');
      else showToast('Product is already in your cabinet', 'info');
    } catch {
      showToast('Failed to add product', 'error');
    }
  };

  const getRoutineIcon = () => {
    if (!customRoutineInfo) return 'heart';
    
    const iconMap: { [key: string]: string } = {
      'post-exercise': 'fitness',
      'back-from-work': 'work',
      'beach-day': 'sun',
      'after-night-out': 'moon'
    };
    
    return iconMap[customRoutineInfo.icon] || 'heart';
  };

  if (isLoading) {
    return (
      <div className="routine-page-container">
        <LoadingSpinner />
      </div>
    );
  }

  if (!customRoutineInfo) {
    return (
      <div className="routine-page-container">
        <Typography variant="h1">Routine not found</Typography>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </div>
    );
  }

  return (
    <div className="routine-page-container">
      <div className="routine-header">
        <div className="routine-header-left">
          <IconButton
            icon={<Icon name="chevron-left" size="large" />}
            onClick={() => navigate('/')}
            ariaLabel="Back to home"
            className="routine-back-button"
          />
          <div className="routine-header-copy">
            <Typography variant="h1">{customRoutineInfo.routine_name}</Typography>
            <Typography variant="body" style={{ color: 'var(--color-dark-gray)' }}>
              {customRoutineInfo.description}
            </Typography>
            {isSaving && (
              <div className="saving-indicator">
                <Icon name="refresh" size="small" className="spinning" />
                <Typography variant="body-small">Saving...</Typography>
              </div>
            )}
          </div>
        </div>
        
        {isMobile ? (
          <IconButton
            icon={<Icon name="adjustments" size="large" />}
            onClick={() => setIsDrawerOpen(true)}
            ariaLabel="Adjust routine settings"
            className="recommendations-icon-button"
          />
        ) : (
          user ? (
            <Toggle
              checked={showRecommendations}
              onChange={(checked) => {
                if (user) {
                  setShowRecommendations(checked);
                  localStorage.setItem('showRecommendations', JSON.stringify(checked));
                }
              }}
              label="Show recommendations"
              id="show-recommendations"
            />
          ) : (
            <Tooltip text="Create account to access feature" position="top">
              <div style={{ display: 'inline-block' }}>
                <Toggle
                  checked={false}
                  onChange={() => {}}
                  label="Show recommendations"
                  id="show-recommendations"
                  disabled={true}
                />
              </div>
            </Tooltip>
          )
        )}
      </div>

      <div className="routine-tabs">
        <NavigationTab
          days={days}
          selected={selectedDay}                    
          onSelect={setSelectedDay}
          showIcon={true}
          iconName="gentle"
          shouldShowIconForDay={(dayIndex) => daysWithoutActives.has(dayIndex)}
        />
      </div>

      <div className="routine-content">
        {routine.length === 0 ? (
          <EmptyCabinetState
            title={`No products in your ${customRoutineInfo.routine_name.toLowerCase()} routine`}
            description={`Add products from your cabinet for ${days[selectedDay]} to get started.`}
            actionLabel="Add to routine"
            onAction={() => setIsAddToRoutineDialogOpen(true)}
          />
        ) : (
          <div className="routine-cards">
            {routine.map((product, index) => {
              const { isCorrectOrder, expectedPosition } = checkProductOrder(
                product, 
                index, 
                routineType!, 
                routine
              );
              
              return (
                <div
                  key={`${product.product_id}-${index}`}
                  className={`routine-card-wrapper ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <RoutineCard
                    company={product.brand}
                    productName={product.product_name}
                    category={product.category}
                    imageSrc={product.image_url}
                    showOrderWarning={!isCorrectOrder}
                    expectedPosition={expectedPosition}
                    onDelete={() => handleDeleteProduct(product.product_id)}
                    product={{
                      product_id: product.product_id,
                      product_name: product.product_name,
                      brand: product.brand,
                      category: product.category,
                      type: product.type,
                      image_url: product.image_url,
                      actives: product.actives,
                      description: product.description,
                      ingredients: product.ingredients,
                    }}
                    onClick={setSelectedProduct}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Responsive Dialog for adding products */}
      <ResponsiveDialog
        type="addToRoutine"
        isOpen={isAddToRoutineDialogOpen}
        onClose={() => setIsAddToRoutineDialogOpen(false)}
        onAddProduct={handleAddProductToRoutine}
        onAddAction={handleAddActionToRoutine}
        routineType={routineType as 'AM' | 'PM'}
        selectedDay={days[selectedDay]}
        currentRoutineProducts={routine}
        userId={user?.id || 0}
      />

      {/* Product detail (from routine card click) */}
      <ResponsiveDialog
        type="productDetail"
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct ?? undefined}
        onAddProductToCabinet={handleAddProductToCabinet}
      />

      {/* Recommendations Bottom Sheet */}
      <RecommendationsBottomSheet
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        routineType={routineType!}
        dayOfWeek={days[selectedDay]}
        recommendations={recommendations}
      />

      {/* Add to Routine Button — the empty state carries its own call to action */}
      {routine.length > 0 && (
        <div className="add-to-routine-button-container">
          <Button
            variant="text"
            onClick={() => setIsAddToRoutineDialogOpen(true)}
            className="add-to-routine-button"
            icon={{ name: "add", size: "small" }}
          >
            Add to Routine
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomRoutine;
